import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatMenuModule } from '@angular/material/menu';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@ngx-translate/core';
import { GlobalModule } from '../global/global.module';
import { SharedModule } from '../request-v2/components/shared/shared.module';
import { SkeletonLoaderModule } from '../global/components/skeleton-loader/skeleton-loader.module';
import { MESSAGE_SERVICE_PROVIDER } from '../global/services/message/message.service';

import { PermitDashboardComponent } from './components/permit-dashboard/permit-dashboard.component';
import { PermitListPageComponent } from './components/permit-list-page/permit-list-page.component';
import { PermitAnalyticsComponent } from './components/permit-analytics/permit-analytics.component';
import { PermitConfigComponent } from './components/permit-config/permit-config.component';
import { PermitSectionBlockComponent } from './components/permit-section-block/permit-section-block.component';
import { PermitStatusSelectComponent } from './components/shared/permit-status-select/permit-status-select.component';
import { PermitSettingsHeaderComponent } from './components/shared/permit-settings-header/permit-settings-header.component';
import { PermitStatusChangeDialogComponent } from './components/dialogs/permit-status-change-dialog/permit-status-change-dialog.component';
import { PermitFilterSidenavComponent } from './components/permit-filter-sidenav/permit-filter-sidenav.component';
import { PermitDetailPageComponent } from './components/permit-detail-page/permit-detail-page.component';
import { PermitInfosPanelComponent } from './components/permit-infos-panel/permit-infos-panel.component';
import { PermitInternalPanelComponent } from './components/permit-internal-panel/permit-internal-panel.component';
import { PermitPrintDialogComponent } from './components/permit-print-dialog/permit-print-dialog.component';
import { PermitInfoComponent } from './components/permit-info/permit-info.component';
import { PermitMessagesComponent } from './components/permit-messages/permit-messages.component';
import { PermitSubtasksComponent } from './components/permit-subtasks/permit-subtasks.component';
import { PermitLinksComponent } from './components/permit-links/permit-links.component';
import { PermitInternalFilesComponent } from './components/permit-internal-files/permit-internal-files.component';
import { PermitInternalNoteComponent } from './components/permit-internal-note/permit-internal-note.component';
import { PermitHistoryComponent } from './components/permit-history/permit-history.component';
import { PermitConfigCategoriesComponent } from './components/permit-config-categories/permit-config-categories.component';
import { PermitSyncProgressDialogComponent } from './components/dialogs/permit-sync-progress-dialog/permit-sync-progress-dialog.component';
import { PermitConfigStatusesComponent } from './components/permit-config-statuses/permit-config-statuses.component';
import { PermitConfigDepartmentsComponent } from './components/permit-config-departments/permit-config-departments.component';
import { PermitConfigTagsComponent } from './components/permit-config-tags/permit-config-tags.component';
import { PermitStatusMigrateDialogComponent } from './components/dialogs/permit-status-migrate-dialog/permit-status-migrate-dialog.component';
import { PermitStatusFormComponent } from './components/permit-status-form/permit-status-form.component';
import { PermitCategoryFormComponent } from './components/permit-category-form/permit-category-form.component';
import { PermitDepartmentFormComponent } from './components/permit-department-form/permit-department-form.component';
import { PermitFormFieldDialogComponent } from './components/dialogs/permit-form-field-dialog/permit-form-field-dialog.component';
import { PermitTypeEditorComponent } from './components/permit-type-editor/permit-type-editor.component';
import { CreatePermitDialogComponent } from './components/create-permit-dialog/create-permit-dialog.component';
import { PermitCategoryStepComponent } from './components/create-permit-dialog/permit-category-step/permit-category-step.component';
import { PermitTypeStepComponent } from './components/create-permit-dialog/permit-type-step/permit-type-step.component';
import { PermitLocationStepComponent } from './components/create-permit-dialog/permit-location-step/permit-location-step.component';
import { PermitResourcesStepComponent } from './components/create-permit-dialog/permit-resources-step/permit-resources-step.component';
import { PermitFilePreviewDialogComponent } from './components/dialogs/permit-file-preview-dialog/permit-file-preview-dialog.component';
import { PermitDetailsStepComponent } from './components/create-permit-dialog/permit-details-step/permit-details-step.component';
import { PermitReviewStepComponent } from './components/create-permit-dialog/permit-review-step/permit-review-step.component';
import { PermitCreatedComponent } from './components/create-permit-dialog/permit-created/permit-created.component';
import { PermitV2Service } from './services/permits.abstract.service';
import { PermitV2ImplService } from './services/permits.impl.service';
import { PermitCategoryService } from './services/permit-category.service';
import { PermitStatusService } from './services/permit-status.service';
import { PermitTagService } from './services/permit-tag.service';
import { PermitAnalyticsService } from './services/permit-analytics.service';
import { PermitConfigRefreshService } from './services/permit-config-refresh.service';
import { PermitDepartmentService } from './services/permit-department.service';
import { PermitSettingsService } from './services/permit-settings.service';
import { PermitMentionPipe } from './pipes/permit-mention.pipe';

@NgModule({
    declarations: [
        PermitDashboardComponent,
        PermitListPageComponent,
        PermitAnalyticsComponent,
        PermitConfigComponent,
        PermitSectionBlockComponent,
        PermitStatusSelectComponent,
        PermitSettingsHeaderComponent,
        PermitStatusChangeDialogComponent,
        PermitFilterSidenavComponent,
        PermitDetailPageComponent,
        PermitSyncProgressDialogComponent,
        PermitInfosPanelComponent,
        PermitInternalPanelComponent,
        PermitPrintDialogComponent,
        PermitInfoComponent,
        PermitMessagesComponent,
        PermitSubtasksComponent,
        PermitLinksComponent,
        PermitInternalFilesComponent,
        PermitInternalNoteComponent,
        PermitMentionPipe,
        PermitHistoryComponent,
        PermitConfigCategoriesComponent,
        PermitConfigStatusesComponent,
        PermitConfigDepartmentsComponent,
        PermitConfigTagsComponent,
        PermitStatusMigrateDialogComponent,
        PermitStatusFormComponent,
        PermitCategoryFormComponent,
        PermitDepartmentFormComponent,
        PermitFormFieldDialogComponent,
        PermitTypeEditorComponent,
        CreatePermitDialogComponent,
        PermitCategoryStepComponent,
        PermitTypeStepComponent,
        PermitLocationStepComponent,
        PermitResourcesStepComponent,
        PermitFilePreviewDialogComponent,
        PermitDetailsStepComponent,
        PermitReviewStepComponent,
        PermitCreatedComponent,
    ],
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        MatTabsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatChipsModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDividerModule,
        MatSidenavModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatCheckboxModule,
        MatRadioModule,
        MatMenuModule,
        DragDropModule,
        TranslateModule,
        GlobalModule,
        SharedModule,
        SkeletonLoaderModule,
    ],
    exports: [
        PermitDashboardComponent,
        PermitListPageComponent,
        PermitAnalyticsComponent,
        PermitConfigComponent,
        PermitSectionBlockComponent,
        PermitStatusSelectComponent,
        PermitFilterSidenavComponent,
        PermitDetailPageComponent,
    ],
    providers: [
        { provide: PermitV2Service, useClass: PermitV2ImplService },
        PermitCategoryService,
        PermitStatusService,
        PermitTagService,
        PermitAnalyticsService,
        PermitConfigRefreshService,
        PermitDepartmentService,
        PermitSettingsService,
        MESSAGE_SERVICE_PROVIDER,
    ],
})
export class PermitModule {}
